import { useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Eye, EyeSlash } from "@phosphor-icons/react";
import axios from "axios";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ForgotPasswordLink() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    if (!email.trim()) return toast.error("Enter your email");
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim() });
      toast.success("If that account exists, a reset link has been emailed.");
      setOpen(false); setEmail("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setLoading(false); }
  };
  return (
    <>
      <button onClick={() => setOpen(true)} className="text-[#1E6BC6] hover:underline text-sm"
              data-testid="forgot-password-link">
        Forgot password
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm rounded-lg">
          <DialogHeader><DialogTitle>Reset your password</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Enter your email and we'll send a reset link (valid for 1 hour).
          </p>
          <div className="mt-3">
            <Label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Email</Label>
            <Input className="mt-1 rounded-md h-11" type="email" value={email}
                   onChange={e => setEmail(e.target.value)}
                   data-testid="forgot-email-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-md" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="rounded-md bg-[#1E6BC6] hover:bg-[#155A9F]" onClick={submit} disabled={loading}
                    data-testid="send-reset-btn">
              {loading ? "Sending…" : "Send reset link"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function Login() {
  const { user, login, register } = useAuth();
  const [mode, setMode] = useState("signin"); // signin | signup
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = mode === "signup"
      ? await register(email.trim(), password, name.trim())
      : await login(email.trim(), password);
    setLoading(false);
    if (!res.ok) toast.error(res.error || (mode === "signup" ? "Sign up failed" : "Login failed"));
    else toast.success(mode === "signup" ? "Account created" : "Welcome back");
  };

  const isSignup = mode === "signup";

  return (
    <div className="min-h-screen bg-[#F3F4F6] flex items-start justify-center px-4 pt-20">
      <div className="w-full max-w-md">
        {/* Brand */}
        <div className="text-center mb-10">
          <div className="text-5xl font-bold tracking-tight leading-none">
            <span className="text-[#1E6BC6]">Spec</span><span className="text-[#F97316]">field</span>
          </div>
          <div className="mt-3 text-[#111827] font-semibold text-sm">
            MANAGEMENT SERVICES SDN. BHD.
          </div>
          <div className="text-xs text-[#9CA3AF] mt-1">202201028670 (1474367-W)</div>
          <div className="mt-4 inline-block font-mono text-[10px] uppercase tracking-[0.25em] text-[#1E6BC6] px-3 py-1 border border-[#1E6BC6]/30 bg-[#E7F0FA] rounded-full">
            Secretary Portal
          </div>
        </div>

        {/* Form */}
        <form onSubmit={submit} className="space-y-3" data-testid="login-form">
          {isSignup && (
            <Input
              id="name" type="text" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Full name"
              className="rounded-md h-12 bg-white border-[#E5E7EB] shadow-sm px-4"
              data-testid="signup-name-input"
            />
          )}

          <Input
            id="email" type="email" required value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="User ID"
            className="rounded-md h-12 bg-white border-[#E5E7EB] shadow-sm px-4"
            data-testid="login-email-input"
          />

          <div className="relative">
            <Input
              id="password" type={showPwd ? "text" : "password"} required
              minLength={isSignup ? 6 : undefined} value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="rounded-md h-12 bg-white border-[#E5E7EB] shadow-sm pl-4 pr-11"
              data-testid="login-password-input"
            />
            <button type="button" onClick={() => setShowPwd(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B7280] hover:text-[#111827]"
                    aria-label={showPwd ? "Hide password" : "Show password"}
                    data-testid="toggle-password-visibility">
              {showPwd ? <EyeSlash size={20} /> : <Eye size={20} />}
            </button>
          </div>
          {isSignup && <p className="text-[11px] text-muted-foreground pl-1">At least 6 characters</p>}

          <Button
            type="submit" disabled={loading}
            className="w-full h-12 mt-5 rounded-md bg-[#1E6BC6] hover:bg-[#155A9F] text-white text-base font-semibold shadow-sm"
            data-testid="login-submit-btn"
          >
            {loading ? (isSignup ? "Creating…" : "Signing in…") : "Submit"}
          </Button>
        </form>

        <div className="mt-6 text-center space-y-3">
          {isSignup ? (
            <button onClick={() => setMode("signin")} className="text-sm text-[#1E6BC6] hover:underline"
                    data-testid="switch-to-signin">
              Already have an account? Sign in
            </button>
          ) : (
            <>
              <div>
                <button onClick={() => setMode("signup")} className="text-sm text-[#1E6BC6] hover:underline"
                        data-testid="switch-to-signup">
                  No account? Create one!
                </button>
              </div>
              <div><ForgotPasswordLink /></div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
